import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
    Flex,
    Label,
    Modal,
    Spinner,
    FormGroup,
    Explainer,
    Paragraph,
    TextInput,
    InputButton
} from '@ubisend/pulse-components';
import Icon from '@ubisend/pulse-icons';
import { useMutation } from '@ubisend/pulse-hooks';

import {
    reverifySharePointSite,
    fetchSharePointTenantId,
    updateSharePointCredential,
    verifySharePointCredential
} from '../../api';
import PreviewSiteTreeWithFilters from './PreviewSiteTreeWithFilters';

const ClampedParagraph = styled(Paragraph)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ danger = false, theme }) => danger && `color: ${theme.danger};`}
  ${({ success = false, theme }) => success && `color: ${theme.positive};`}
`;

const ErrorExplainer = ({ errorMessage, fadeError }) => {
    if (!errorMessage) {
        return null;
    }

    return (
        <Explainer
            type="danger"
            mb
            hideLabel
            style={{ opacity: fadeError ? 0 : 1, transition: 'opacity 1s ease-out' }}
        >
            <ClampedParagraph sm danger>
                {errorMessage}
            </ClampedParagraph>
        </Explainer>
    );
};

ErrorExplainer.propTypes = {
    errorMessage: PropTypes.string,
    fadeError: PropTypes.bool
};

const UpdateCredentialModal = ({ state }) => {
    const [formData, setFormData] = useState({
        tenantId: '',
        clientId: '',
        clientSecret: '',
        showClientSecret: false
    });

    const [errorMessage, setErrorMessage] = useState(null);
    const [fadeError, setFadeError] = useState(false);
    const [step, setStep] = useState('input');
    const [previewData, setPreviewData] = useState([]);
    const [loadingTenantId, setLoadingTenantId] = useState(true);
    const hasRetriedValidate = useRef(false);

    useEffect(() => {
        fetchSharePointTenantId()
            .then(res => {
                setFormData(prev => ({
                    ...prev,
                    tenantId: res.data.tenant_id,
                    clientId: res.data.client_id
                }));
            })
            .catch(() => {
                setErrorMessage('Failed to fetch Tenant ID.');
            })
            .finally(() => {
                setLoadingTenantId(false);
            });
    }, []);

    const handleChange = field => e => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const toggleClientSecretVisibility = () => {
        setFormData(prev => ({ ...prev, showClientSecret: !prev.showClientSecret }));
    };

    const isFormValid = useMemo(() => {
        return formData.tenantId && formData.clientId && formData.clientSecret;
    }, [formData]);

    const verifyMutation = useMutation({ mutationFn: reverifySharePointSite });

    const validateMutation = useMutation({
        mutationFn: verifySharePointCredential,
        onSuccess: async ({ data }) => {
            const sites = data.sites || [];
            const nonPersonalSites = sites.filter(site => !site.is_personal_site);

            const enrichedSites = await Promise.all(
                nonPersonalSites.map(async site => {
                    try {
                        const res = await verifyMutation.mutateAsync({
                            client_id: formData.clientId,
                            client_secret: formData.clientSecret,
                            site_id: site.site_id,
                            site_name: site.site_name,
                            is_personal_site: 0
                        });

                        const folders = res.data || [];
                        const totalFileCount = folders.reduce(
                            (sum, folder) => sum + (folder.file_count || 0),
                            0
                        );

                        return { ...site, folders, file_count: totalFileCount };
                    } catch {
                        return { ...site, folders: [], file_count: 0 };
                    }
                })
            );

            setPreviewData(enrichedSites);
            setStep('preview');
            hasRetriedValidate.current = false;
        },
        onError: (error, variables) => {
            const statusCode = error?.code;
            const backendMessage = error?.response?.data?.message || error?.message || '';
            const allowedMessages = ['Invalid credential provided.'];

            const message = allowedMessages.includes(backendMessage)
                ? backendMessage
                : 'Sorry, something went wrong. Please contact your account manager if the problem persists.';

            if (statusCode === 'ERR_NETWORK' && !hasRetriedValidate.current) {
                hasRetriedValidate.current = true;
                setTimeout(() => {
                    validateMutation.mutate(variables);
                }, 100);
                return;
            }

            setErrorMessage(message);
            setFadeError(false);
            setTimeout(() => {
                setFadeError(true);
                setTimeout(() => {
                    setErrorMessage(null);
                }, 1000);
            }, 10000);
        }
    });

    const confirmMutation = useMutation({
        mutationFn: updateSharePointCredential,
        onSuccess: () => {
            state.setActiveModal(null);
            state.setTokenInvalid(false);
            setTimeout(() => { state.refetchStatus(); }, 500);
        },
        onError: error => {
            const backendMessage = error?.response?.data?.message || error?.message || '';
            const allowedMessages = ['Invalid credential provided.'];
            const message = allowedMessages.includes(backendMessage)
                ? backendMessage
                : 'Sorry, something went wrong. Please contact your account manager if the problem persists.';

            setErrorMessage(message);
            setFadeError(false);
            setTimeout(() => {
                setFadeError(true);
                setTimeout(() => {
                    setErrorMessage(null);
                }, 1000);
            }, 10000);
        }
    });

    const handleValidate = () => {
        validateMutation.mutate({
            client_id: formData.clientId,
            client_secret: formData.clientSecret
        });
    };

    const handleConfirm = () => {
        confirmMutation.mutate({
            client_id: formData.clientId,
            client_secret: formData.clientSecret
        });
    };

    const modalLoading = confirmMutation.isPending || validateMutation.isPending || loadingTenantId;

    let contentBody;
    if (loadingTenantId) {
        contentBody = (
            <Flex col center middle tall>
                <Spinner />
                <Paragraph mt>Loading Credential...</Paragraph>
            </Flex>
        );
    } else if (step === 'input') {
        contentBody = (
            <form onSubmit={e => e.preventDefault()}>
                <FormGroup>
                    <Label htmlFor="tenantId">Tenant ID</Label>
                    <TextInput id="tenantId" disabled value={formData.tenantId} />
                    <Paragraph sm secondary>
                        To change the tenant ID, please disconnect and reconnect.
                    </Paragraph>
                </FormGroup>
                <FormGroup>
                    <Label htmlFor="clientId">Client ID</Label>
                    <TextInput
                        id="clientId"
                        placeholder="Please enter your client id."
                        value={formData.clientId}
                        disabled={modalLoading}
                        onChange={handleChange('clientId')}
                    />
                </FormGroup>
                <FormGroup>
                    <Label htmlFor="clientSecret">Client Secret</Label>
                    <Flex relative>
                        <TextInput
                            id="clientSecret"
                            type={formData.showClientSecret ? 'text' : 'password'}
                            placeholder="Please enter your client secret."
                            value={formData.clientSecret}
                            disabled={modalLoading}
                            onChange={handleChange('clientSecret')}
                        />
                        <InputButton
                            type="button"
                            aria-label={`${formData.showClientSecret ? 'hide' : 'show'} client secret`}
                            onClick={toggleClientSecretVisibility}
                        >
                            <Icon
                                size="1.25rem"
                                width="1.25rem"
                                height="1.25rem"
                                type={formData.showClientSecret ? 'eye' : 'eyeOff'}
                            />
                        </InputButton>
                    </Flex>
                </FormGroup>
            </form>
        );
    } else if (step === 'preview') {
        contentBody = <PreviewSiteTreeWithFilters data={previewData} isLoading={modalLoading} />;
    }

    return (
        <Modal
            header="Update SharePoint Credentials"
            handleCancel={() => state.setActiveModal(null)}
            handleConfirm={step === 'preview' ? handleConfirm : handleValidate}
            disableConfirmButton={!isFormValid}
            loading={modalLoading}
            confirmText={step === 'preview' ? 'Confirm' : 'Verify Credential'}
            cancelText="Cancel"
            body={
                <>
                    <ErrorExplainer errorMessage={errorMessage} fadeError={fadeError} />
                    {contentBody}
                </>
            }
        />
    );
};

UpdateCredentialModal.propTypes = {
    state: PropTypes.shape({
        refetchStatus: PropTypes.func.isRequired,
        setActiveModal: PropTypes.func.isRequired,
        setTokenInvalid: PropTypes.func.isRequired,
    }).isRequired
};

export default UpdateCredentialModal;